<template>
  <div class="option-list">
    <div
      v-if="!isUploadingOrPending"
      class="option-list__add-option"
    >
      <StitchTooltip
        :is-disabled="canManageOptions"
        :message="manageOptionsDisabledMessages.ADD"
        placement="top"
      >
        <ElementButton
          ref="addOptionButton"
          :disabled="!canManageOptions || isNotAllowedToAddVersions"
          type="text"
          @click="emitAddOption"
        >
          Add New Colorway
        </ElementButton>
        <template v-if="isNotAllowedToAddVersions">
          <ElementTooltip
            effect="dark"
            :content="`Adding colorways with the status '${styleStatus}' is not allowed.`"
            placement="right-start"
            :open-delay="400"
            :enterable="false"
          >
            <i class="el-icon-info" />
          </ElementTooltip>
        </template>
      </StitchTooltip>
      <ElementInput
        ref="searchInput"
        v-model="optionSearchValue"
        placeholder="Search for color code"
        size="small"
        clearable
        class="option-list__search"
      >
        <i
          slot="prefix"
          class="el-input__icon el-icon-search"
        />
      </ElementInput>
    </div>
    <div
      ref="optionList"
      class="option-list__options"
    >
      <div
        v-if="isStuck"
        class="render-status render-status--stuck"
      >
        <!--
          RESTRICTION: this panel renders inside the short colorway rectangle, so
          the copy is capped at two lines (icon + title, then the action). Extra
          lines are clipped by the pane height — do not add any without revisiting
          the available height.
        -->
        <h2 class="render-status__title">
          <i class="el-icon-warning-outline" />
          This render got stuck.
        </h2>
        <p>
          Re-render this version, or email us at
          <a href="mailto:3Dsupport@pvh.com">3Dsupport@pvh.com</a>.
        </p>
      </div>
      <div
        v-else-if="showError"
        class="render-status"
      >
        <h2>{{ errorMessage.title }}</h2>
        <p>{{ errorMessage.instruction }}</p>
        <p>
          Need help? Email us at
          <a href="mailto:3Dsupport@pvh.com">3Dsupport@pvh.com</a>.
        </p>
      </div>
      <template v-else>
        <div
          v-if="isUploadingOrPending"
          ref="loader"
          class="option-item option-item__option--rendering"
        >
          <OptionRenderSteps
            v-if="version.optionsLoaded"
            :version="version"
          />

          <div
            v-else
            class="option-list__skeletons"
          >
            <div
              v-for="index in 4"
              :key="index"
              class="option-list__skeleton"
            >
              <div
                class="option-list__skeleton-line option-list__skeleton-line--title"
              />
              <div
                class="option-list__skeleton-line option-list__skeleton-line--medium"
              />
            </div>
          </div>
        </div>
        <Draggable
          v-else
          ghost-class="option-list__dragged"
          v-bind="{ animation: 150 }"
          @end="onSortDrop"
          @start="disableDragDropUpload"
        >
          <OptionItem
            v-for="option in filteredOptions"
            :id="option.id"
            :key="option.id"
            :option="option"
            :version="version"
            class="option-list__option"
            @update-option="handleUpdateOption"
            @delete-option="handleDeleteOption"
            @add-images="handleAddImages"
            @update-image="handleUpdateImage"
            @delete-image="handleDeleteImage"
          />
        </Draggable>
      </template>
    </div>
  </div>
</template>

<script>
import Draggable from 'vuedraggable'
import { mapActions, mapGetters } from 'vuex'

import {
  JOB_STATUS,
  RENDER_JOB_FAILED_MESSAGE
} from '@/constants/loadingStatus'

import VueTypes from 'vue-types'
import { OptionShape, VersionShape } from '@/types'
import OptionItem from './components/OptionItem'
import OptionRenderSteps from './components/OptionRenderSteps'
import { USER_ROLE } from '@/constants/roleType'
import { ASSET_STATUS } from '@/constants/assetStatuses'

export default {
  name: 'OptionList',

  components: {
    Draggable,
    OptionItem,
    OptionRenderSteps
  },

  props: {
    activeVersion: VueTypes.objectOf(VersionShape).loose,
    options: VueTypes.arrayOf(OptionShape),
    styleStatus: VueTypes.string
  },

  emits: [
    'add-option',
    'update-option',
    'delete-option',
    'add-option-images',
    'update-option-image',
    'delete-option-image'
  ],

  data () {
    return {
      manageOptionsDisabledMessages: {
        ADD: [
          'This version comes from a .BW file. Add a colorway',
          'on the 3D file and upload it as a new version.'
        ]
      },
      errorMessages: {
        NO_OPTIONS: 'Please Add New Colorway'
      },
      isSorting: false,
      optionSearchValue: '',
      helpCenterLink:
        'https://hub-support.freshdesk.com/support/solutions/articles/101000446535-browzwear-file-fails-to-upload'
    }
  },

  computed: {
    ...mapGetters(['getVersionById']),

    ...mapGetters({
      user: 'getCognitoUserData'
    }),

    /**
     * @returns {object}
     */
    version () {
      const version = this.getVersionById(this.activeVersion.id)

      if (this.activeVersion.isUploading || !version) {
        return this.activeVersion
      }

      return version
    },

    /**
     * @returns {Array}
     */
    filteredOptions () {
      return this.options.filter(
        option =>
          (option.name &&
            option.name
              .toLowerCase()
              .includes(this.optionSearchValue.toLowerCase())) ||
          (option.code &&
            option.code
              .toLowerCase()
              .includes(this.optionSearchValue.toLowerCase()))
      )
    },

    /**
     * @returns {boolean}
     */
    isVersionEmpty () {
      return this.options.length === 0
    },

    /**
     * @returns {boolean}
     */
    isVendorUser () {
      return this.user.role === USER_ROLE.VENDOR.name
    },

    /**
     * @returns {boolean}
     */
    isNotAllowedToAddVersions () {
      return (
        this.isVendorUser &&
        [
          ASSET_STATUS.REVIEW.label,
          ASSET_STATUS.APPROVED.label,
          ASSET_STATUS.APPROVED_FOR_DAM.label,
          ASSET_STATUS.CANCELLED.label,
          ASSET_STATUS.PENDING.label
        ].includes(this.styleStatus)
      )
    },

    /**
     * @returns {boolean}
     */
    showError () {
      return (
        this.version.autorenderIsStuck ||
        this.version.autorenderStatus === JOB_STATUS.FAILED ||
        (this.isVersionEmpty && !this.isUploadingOrPending)
      )
    },

    /**
     * Reports whether the render farm flagged this version's job as stuck.
     * Drives a dedicated warning panel that is distinct from a hard failure.
     *
     * @returns {boolean}
     */
    isStuck () {
      return this.version.autorenderIsStuck
    },

    /**
     * @returns {boolean}
     */
    canManageOptions () {
      return (
        this.version.model === null ||
        (this.version.model && !this.version.model.endsWith('.bw'))
      )
    },

    /**
     * @returns {object}
     */
    errorMessage () {
      if (!this.version.model) {
        return RENDER_JOB_FAILED_MESSAGE.NO_3D_FILE
      } else {
        const renderErrorMessage = this.version.autorenderStatusMessage
        switch (renderErrorMessage) {
          case RENDER_JOB_FAILED_MESSAGE.SNAPSHOT_ERROR.renderFarmMessage:
            return RENDER_JOB_FAILED_MESSAGE.SNAPSHOT_ERROR
          case RENDER_JOB_FAILED_MESSAGE.NO_SNAPSHOTS.renderFarmMessage:
            return RENDER_JOB_FAILED_MESSAGE.NO_SNAPSHOTS
          case RENDER_JOB_FAILED_MESSAGE.NO_AVATAR.renderFarmMessage:
            return RENDER_JOB_FAILED_MESSAGE.NO_AVATAR
          default:
            return RENDER_JOB_FAILED_MESSAGE.DEFAULT
        }
      }
    },

    /**
     * @returns {boolean}
     */
    isUploadingOrPending () {
      const { autorenderStatus, isUploading, optionsLoaded } = this.version

      return (
        isUploading ||
        autorenderStatus === JOB_STATUS.STARTED ||
        autorenderStatus === JOB_STATUS.PENDING ||
        !optionsLoaded
      )
    }
  },

  methods: {
    ...mapActions(['updateOption']),

    /**
     */
    emitAddOption () {
      this.$emit('add-option', { versionId: this.version.id })
    },

    /**
     * @param {object} payload
     */
    handleUpdateOption (payload) {
      this.$emit('update-option', payload)
    },

    /**
     * @param {object} payload
     */
    handleDeleteOption (payload) {
      this.$emit('delete-option', payload)
    },

    /**
     * @param {object} payload
     */
    handleAddImages (payload) {
      this.$emit('add-option-images', payload)
    },

    /**
     * @param {object} payload
     */
    handleUpdateImage (payload) {
      this.$emit('update-option-image', payload)
    },

    /**
     * @param {object} payload
     */
    handleDeleteImage (payload) {
      this.$emit('delete-option-image', payload)
    },

    /**
     * @param {Event} event
     */
    onSortDrop (event) {
      if (event.oldIndex !== event.newIndex) {
        const option = this.options[event.oldIndex]
        const payload = {
          styleId: this.activeVersion.styleId,
          versionId: option.versionId,
          optionId: option.id,
          index: event.newIndex
        }

        this.updateOption(payload)
        this.isSorting = false
      }
    },

    /**
     */
    disableDragDropUpload () {
      this.isSorting = true
    }
  }
}
</script>

<style lang="scss" scoped>
$border-radius-options: $border-radius-s * 2;
$render-status-icon-font-size: 38px;
$skeleton-line-width-title: 45%;
$skeleton-line-width-short: 30%;
$skeleton-line-width-medium: 65%;
$border-radius-circle: 50%;

.option-list {
  display: flex;
  flex-direction: column;
  min-width: spacing(80);
  overflow: hidden;
  border-right: $border-divider;
}

.option-list__add-option,
.option-list__options {
  padding: 0 spacing(2);
}

.option-list__add-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding-bottom: spacing(1);
  border-bottom: $border-divider;
}

.option-list__search {
  width: spacing(25);
}

.option-list__options {
  @include scroll-y-smooth;

  position: relative;
  height: 100%;
  max-height: 100%;
  padding-top: spacing(1.5);
  padding-bottom: spacing(12);

  .sortable-drag {
    background-color: $white;
  }
}

.option-list__option {
  position: relative;
  margin-bottom: spacing(1);
  padding: spacing(2);
  border: $border-divider;
  border-radius: $border-radius-options;
}

.option-item__dragged {
  height: spacing(12);
  visibility: hidden;
}

.option-item__option--rendering {
  display: flex;
  align-items: center;
}

.option-list__skeletons {
  width: 100%;
}

.option-list__skeleton {
  margin-bottom: spacing(1);
  padding: spacing(2);
  background: $white;
  border: $border-divider;
  border-radius: $border-radius-options;
}

.option-list__skeleton-line {
  height: spacing(1.5);
  margin-bottom: spacing(1);
  background: rgba($grey-dark, 0.08);
  border-radius: $border-radius-s;

  &:last-child {
    margin-bottom: 0;
  }
}

.option-list__skeleton-line--title {
  width: percentage(40);
}

.option-list__skeleton-line--medium {
  width: percentage(70);
}

.render-status {
  @include position-absolute;

  width: 100%;
  margin: spacing(1);
  padding: spacing(1);
  color: $grey-dark;
  text-align: center;
  word-break: break-word;

  > * {
    margin-bottom: spacing(1);

    &:last-child {
      margin-bottom: 0;
    }
  }

  a {
    color: $blue;
  }
}

// Amber treatment sets a stalled render apart from the neutral error panel.
.render-status--stuck {
  // Pinned to the pane bounds and centred with flexbox so the panel matches the
  // pane size exactly and never produces a scrollbar.
  //
  // RESTRICTION: this renders inside the short colorway rectangle, so the copy is
  // capped at two lines (icon + title, then the action). Content taller than the
  // pane is clipped — do not add lines or enlarge the icon without revisiting the
  // available height.
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  // "safe center" keeps the message centred when the pane is tall enough and
  // falls back to top-aligned on a short pane, so the title is never clipped.
  justify-content: safe center;
  width: auto;
  margin: 0;
  overflow: hidden;
  transform: none;

  .render-status__title {
    display: flex;
    align-items: center;
    color: $orange;
  }

  // Icon sits inline to the left of the title at the title's font-size to keep
  // the message within two lines.
  i {
    margin-right: spacing(1);
    color: $orange;
  }
}
</style>
